import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SiteLayout } from "@/components/SiteLayout";
import { NewsletterSignup } from "@/components/newsletter/NewsletterSignup";
import { useSite } from "@/hooks/use-site";
import { isScheduledPost, listPosts } from "@/lib/blog";
import { localizeUrl } from "@/lib/site";
import { formatPostDate } from "@/utils/date";
import inextLogo from "@/assets/inext-logo.svg";
import { BlogPostCard, DraftBadge, MediaBadges, ScheduledBadge } from "./BlogPostCard";
import { BLOG_TAG_LABELS } from "./tag-labels";

const TAG_KEYS = Object.keys(BLOG_TAG_LABELS);

export function BlogIndexPage() {
  const { site, messages, href } = useSite();
  const posts = listPosts(site);

  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [visibleCount, setVisibleCount] = useState(12);

  const filteredPosts = activeTag ? posts.filter((p) => p.tags.includes(activeTag)) : posts;

  const showFeatured = activeTag === null && filteredPosts.length > 0;
  const featuredPost = showFeatured ? filteredPosts[0] : null;
  const gridPosts = showFeatured ? filteredPosts.slice(1) : filteredPosts;

  const visibleGridPosts = gridPosts.slice(0, visibleCount);
  const initialGridPosts = visibleGridPosts.slice(0, 3);
  const remainingGridPosts = visibleGridPosts.slice(3);
  const hasMore = visibleGridPosts.length < gridPosts.length;

  return (
    <SiteLayout>
      <PageBreadcrumb items={[{ label: messages.navBlog }]} />
      <section className="relative mx-auto max-w-6xl px-6 py-20 sm:py-28">
        {/* Glow Background */}
        <div className="pointer-events-none absolute inset-0 -z-10 flex justify-center overflow-hidden">
          <div className="h-[400px] w-[800px] max-w-full translate-y-[-20%] rounded-full bg-primary/10 blur-[100px]" />
        </div>

        <div className="flex flex-col items-center text-center">
          <p
            className="inline-flex animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/20"
            style={{ animationDelay: "0ms" }}
          >
            {messages.navBlog}
          </p>
          <h1 className="mt-6 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both text-4xl font-extrabold tracking-tight sm:text-6xl lg:text-7xl">
            <span className="bg-gradient-to-br from-foreground to-foreground/60 bg-clip-text text-transparent">
              {messages.blogTitle}
            </span>
          </h1>
          <p
            className="mt-6 max-w-2xl animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both text-lg text-muted-foreground sm:text-xl"
            style={{ animationDelay: "150ms" }}
          >
            {messages.blogDescription}
          </p>
        </div>

        {/* Filter chips */}
        <div
          className="mt-12 flex flex-wrap justify-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both"
          style={{ animationDelay: "225ms" }}
        >
          <button
            onClick={() => {
              setActiveTag(null);
              setVisibleCount(12);
            }}
            className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              activeTag === null
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
            }`}
          >
            {messages.blogFilterAll}
          </button>
          {TAG_KEYS.map((tag) => (
            <button
              key={tag}
              onClick={() => {
                setActiveTag(tag);
                setVisibleCount(12);
              }}
              className={`cursor-pointer rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                activeTag === tag
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
              }`}
            >
              {messages[BLOG_TAG_LABELS[tag] as keyof typeof messages] as string}
            </button>
          ))}
        </div>

        <div className="mt-12 grid gap-8">
          {/* Featured Post */}
          {featuredPost && (
            <a
              href={localizeUrl(`/blog/${featuredPost.slug}`, site.locale, href)}
              className="group flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[var(--shadow-elevated)] transition-all duration-500 hover:-translate-y-1 hover:border-primary/30 hover:shadow-glow lg:relative lg:flex-row animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
              style={{ animationDelay: "300ms" }}
            >
              <div className="relative aspect-video w-full shrink-0 overflow-hidden bg-muted lg:w-3/5">
                {featuredPost.heroImage ? (
                  <img
                    src={featuredPost.heroImage}
                    alt={featuredPost.title}
                    className="absolute inset-0 h-full w-full object-cover object-center"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-muted/50">
                    <img src={inextLogo} alt="inext" className="h-12 w-auto opacity-20" />
                  </div>
                )}
                <MediaBadges post={featuredPost} messages={messages} />
              </div>
              <div className="flex flex-col justify-center p-6 lg:absolute lg:inset-y-0 lg:right-0 lg:w-2/5 lg:overflow-hidden lg:p-6 xl:p-10">
                <p className="flex shrink-0 items-center gap-2 text-sm font-medium text-primary">
                  {!featuredPost.published && <DraftBadge />}
                  {featuredPost.published && isScheduledPost(featuredPost) && <ScheduledBadge />}
                  <span>{messages.blogFeatured}</span>
                  <span className="text-muted-foreground/50">•</span>
                  <span className="text-muted-foreground">
                    {formatPostDate(featuredPost.publishedAt, site.locale)}
                  </span>
                </p>
                <h2 className="mt-4 line-clamp-3 shrink-0 text-2xl font-bold sm:text-3xl lg:mt-3 lg:text-3xl xl:mt-4">
                  {featuredPost.title}
                </h2>
                <p className="mt-4 line-clamp-3 text-base text-muted-foreground sm:text-lg lg:mt-3 lg:line-clamp-2 lg:shrink-0 lg:text-base xl:mt-4 xl:text-lg">
                  {featuredPost.excerpt}
                </p>
                <div className="mt-6 flex shrink-0 items-center gap-2 text-sm font-medium text-primary lg:mt-4 xl:mt-6">
                  {messages.blogReadMore}{" "}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </div>
            </a>
          )}

          {/* Grid */}
          {initialGridPosts.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {initialGridPosts.map((post, index) => (
                <BlogPostCard
                  key={post.slug}
                  post={post}
                  href={localizeUrl(`/blog/${post.slug}`, site.locale, href)}
                  messages={messages}
                  animationDelay={(featuredPost ? 375 : 300) + index * 50}
                />
              ))}
            </div>
          )}

          {filteredPosts.length > 0 && <NewsletterSignup source="blog-index" variant="blog" />}

          {remainingGridPosts.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {remainingGridPosts.map((post, index) => (
                <BlogPostCard
                  key={post.slug}
                  post={post}
                  href={localizeUrl(`/blog/${post.slug}`, site.locale, href)}
                  messages={messages}
                  animationDelay={(featuredPost ? 375 : 300) + (index + 3) * 50}
                />
              ))}
            </div>
          )}

          {/* Load More */}
          {hasMore && (
            <div
              className="mt-4 flex justify-center animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both"
              style={{
                animationDelay: `${(featuredPost ? 375 : 300) + visibleGridPosts.length * 50}ms`,
              }}
            >
              <button
                onClick={() => setVisibleCount((c) => c + 12)}
                className="cursor-pointer rounded-full bg-secondary px-6 py-2.5 text-sm font-medium text-secondary-foreground transition-colors hover:bg-secondary/80"
              >
                {messages.blogLoadMore}
              </button>
            </div>
          )}

          {/* Empty state */}
          {filteredPosts.length === 0 && (
            <p
              className="mt-8 text-center text-muted-foreground animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both"
              style={{ animationDelay: "300ms" }}
            >
              {messages.blogEmpty}
            </p>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
